import { ServiceType } from '../types';
import type { MachineFailureClassification } from '../types';

export interface FailureDisplayItem {
  label: string;
  raw: string | null;
}

/**
 * Sceglie la label per la lingua corrente (IT se disponibile e lingua italiana, altrimenti EN
 * con fallback sull'altra se una delle due manca) e restituisce solo le dimensioni per cui una
 * label e' stata risolta — se il codice non e' stato determinato dall'LLM non c'e' nulla da
 * mostrare come testo principale (il testo raw resta disponibile solo come tooltip, mai come
 * fallback visibile: e' una scelta esplicita, non un'omissione).
 */
export function getFailureDisplayItems(
  classification: MachineFailureClassification,
  language: string
): FailureDisplayItem[] {
  const isItalian = language.toLowerCase().startsWith('it');
  const pick = (labelIt?: string, labelEn?: string): string | undefined =>
    (isItalian ? labelIt ?? labelEn : labelEn ?? labelIt) || undefined;

  const dimensions: Array<{ label?: string; raw?: string }> = [
    { label: pick(classification.componentLabelIt, classification.componentLabelEn), raw: classification.componentRaw },
    { label: pick(classification.symptomLabelIt, classification.symptomLabelEn), raw: classification.symptomRaw },
    { label: pick(classification.causeLabelIt, classification.causeLabelEn), raw: classification.causeRaw },
    { label: pick(classification.actionLabelIt, classification.actionLabelEn), raw: classification.actionRaw },
  ];

  return dimensions
    .filter((d): d is { label: string; raw?: string } => Boolean(d.label))
    .map((d) => ({ label: d.label, raw: d.raw ?? null }));
}

/**
 * Descrizione di fallback da mostrare quando per una macchina non c'e' nessuna etichetta di
 * guasto risolta (nessuna classificazione, o classificazione presente ma senza codici
 * riconosciuti). Le installazioni macchina (service_type = TYPE1) non sono guasti: per queste
 * si mostra "Machine installation" invece di "Unclassified failure".
 */
export function getUnclassifiedFailureLabel(
  serviceType: string | undefined,
  t: (key: string) => string
): string {
  return serviceType === ServiceType.TYPE1
    ? t('reportForm.machineInstallation')
    : t('reportForm.failureUnclassified');
}
